import { useEffect, useRef, useState } from 'react'

interface UseLoaderVisibilityOptions {
  active: boolean
  showDelayMs?: number
  minVisibleMs?: number
}

export function useLoaderVisibility({
  active,
  showDelayMs = 240,
  minVisibleMs = 400,
}: UseLoaderVisibilityOptions) {
  const [visible, setVisible] = useState(false)
  const shownAtRef = useRef<number | null>(null)
  const showTimerRef = useRef<number | null>(null)
  const hideTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const clearShowTimer = () => {
      if (showTimerRef.current != null) {
        window.clearTimeout(showTimerRef.current)
        showTimerRef.current = null
      }
    }

    const clearHideTimer = () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
    }

    if (active) {
      clearHideTimer()
      if (!visible && showTimerRef.current == null) {
        showTimerRef.current = window.setTimeout(() => {
          shownAtRef.current = Date.now()
          setVisible(true)
          showTimerRef.current = null
        }, showDelayMs)
      }
      return () => {
        clearShowTimer()
        clearHideTimer()
      }
    }

    clearShowTimer()
    if (!visible) {
      return () => {
        clearHideTimer()
      }
    }

    const shownAt = shownAtRef.current ?? Date.now()
    const elapsed = Date.now() - shownAt
    const remaining = Math.max(minVisibleMs - elapsed, 0)
    hideTimerRef.current = window.setTimeout(() => {
      shownAtRef.current = null
      hideTimerRef.current = null
      setVisible(false)
    }, remaining)

    return () => {
      clearHideTimer()
    }
  }, [active, minVisibleMs, showDelayMs, visible])

  return visible
}
